/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  DependencyImportTypes,
  DependencyScopes,
  L10nMessageType,
  PackageManagers,
  ReleaseDetails,
  ReleaseRiskQuality,
  ReleaseRiskSeverity,
  ReleaseRiskType,
  RiskStatus,
  RiskTransitions,
} from '../types';

/** From most to least severe */
export const RISK_SEVERITY_ORDER = [
  ReleaseRiskSeverity.Blocker,
  ReleaseRiskSeverity.High,
  ReleaseRiskSeverity.Medium,
  ReleaseRiskSeverity.Low,
  ReleaseRiskSeverity.Info,
] as const;

export const RISK_TRANSITION_ORDER = [
  RiskTransitions.Reopen,
  RiskStatus.Open,
  RiskTransitions.Confirm,
  RiskStatus.Confirm,
  RiskTransitions.Accept,
  RiskStatus.Accept,
  RiskTransitions.Safe,
  RiskStatus.Safe,
  RiskTransitions.Fixed,
  RiskStatus.Fixed,
] as const;

export const RISK_TYPE_LABEL: Record<ReleaseRiskType, L10nMessageType> = {
  [ReleaseRiskType.Vulnerability]: 'dependencies.risks.type.vulnerability',
  [ReleaseRiskType.ProhibitedLicense]: 'dependencies.risks.type.license',
  [ReleaseRiskType.Malware]: 'dependencies.risks.type.malware',
};

export const PACKAGE_MANAGER_LABELS: Record<PackageManagers, string> = {
  [PackageManagers.Cargo]: 'dependencies.package_managers.CARGO',
  [PackageManagers.Cocoapods]: 'dependencies.package_managers.COCOAPODS',
  [PackageManagers.Composer]: 'dependencies.package_managers.COMPOSER',
  [PackageManagers.Conan]: 'dependencies.package_managers.CONAN',
  [PackageManagers.Gopkg]: 'dependencies.package_managers.GOLANG',
  [PackageManagers.Maven]: 'dependencies.package_managers.MAVEN',
  [PackageManagers.Npm]: 'dependencies.package_managers.NPM',
  [PackageManagers.Nuget]: 'dependencies.package_managers.NUGET',
  [PackageManagers.Pypi]: 'dependencies.package_managers.PYPI',
  [PackageManagers.Rubygems]: 'dependencies.package_managers.GEM',
  [PackageManagers.Vcpkg]: 'dependencies.package_managers.VCPKG',
  [PackageManagers.UNKNOWN]: 'dependencies.package_managers.UNKNOWN',
};

export function getPackageManagerLabel(packageManager: PackageManagers | string) {
  if (packageManager in PACKAGE_MANAGER_LABELS) {
    return PACKAGE_MANAGER_LABELS[packageManager as PackageManagers];
  }
  return packageManager;
}

export const RiskStatusLabels = {
  [RiskStatus.Accept]: {
    label: 'dependencies.risks.status.accept',
    description: 'dependencies.risks.status.accept.description',
  },
  [RiskStatus.Confirm]: {
    label: 'dependencies.risks.status.confirm',
    description: 'dependencies.risks.status.confirm.description',
  },
  [RiskStatus.Open]: {
    label: 'dependencies.risks.status.open',
    description: 'dependencies.risks.status.open.description',
  },
  [RiskStatus.Fixed]: {
    label: 'dependencies.risks.status.fixed',
    description: 'dependencies.risks.status.fixed.description',
  },
  [RiskStatus.Safe]: {
    label: 'dependencies.risks.status.safe',
    description: 'dependencies.risks.status.safe.description',
  },
  [RiskTransitions.Reopen]: {
    label: 'dependencies.risks.status.open',
    description: 'dependencies.risks.status.open.description',
  },
};

export function riskTransitionRequiresComment(transition: RiskTransitions) {
  return transition === RiskTransitions.Safe || transition === RiskTransitions.Accept;
}

export const DIRECT_LABELS: Record<DependencyImportTypes, Partial<L10nMessageType>> = {
  direct: 'dependencies.properties.import_type.direct',
  transitive: 'dependencies.properties.import_type.transitive',
};

export const RISK_QUALITY_ORDER = [ReleaseRiskQuality.Security, ReleaseRiskQuality.Maintainability];

const PACKAGE_NAME_CHUNK_SEPARATOR = '...';
export const PACKAGE_NAME_MAX_LENGTH = 60;
export const VERSION_MAX_LENGTH = 20;
export function truncateMiddle(value: string, maxLength = 25) {
  if (value.length <= maxLength) {
    return {
      value,
      truncated: false,
    };
  }

  const halfLength = Math.floor(maxLength / 2 - PACKAGE_NAME_CHUNK_SEPARATOR.length / 2);
  return {
    value: `${value.slice(0, halfLength)}${PACKAGE_NAME_CHUNK_SEPARATOR}${value.slice(-halfLength)}`,
    truncated: true,
  };
}

export function formatRelease(release: Pick<ReleaseDetails, 'packageName' | 'version'>) {
  return `${release.packageName} ${release.version}`;
}

export function parseFilePath(filepath: string) {
  // Handle both forward and back slashes
  const normalizedPath = filepath.replace(/\\/g, '/');
  // Avoid super-linear regex by using lastIndexOf instead of regex
  const lastSlash = normalizedPath.lastIndexOf('/');
  if (lastSlash !== -1) {
    return {
      parent: normalizedPath.slice(0, lastSlash),
      basename: normalizedPath.slice(lastSlash + 1),
    };
  }
  return {
    parent: '',
    basename: normalizedPath,
  };
}

export function getStringSearchParam<FilterType extends string>(
  params: URLSearchParams,
  key: FilterType,
): string | undefined {
  const param = params.get(key);

  if (param === null) {
    return undefined;
  }

  return param;
}

export function getSingleValueSearchParam<FilterType extends string, T extends string>(
  params: URLSearchParams,
  key: FilterType,
  enumType: { [key: string]: T },
): T | undefined {
  const param = params.get(key);
  if (param === null) {
    return undefined;
  } else if (Object.values(enumType).includes(param as T)) {
    return param as T;
  }
  return undefined;
}

export function getMultipleValuesGeneric<FilterType extends string>(
  params: URLSearchParams,
  key: FilterType,
) {
  const param = params.get(key);
  if (param === null) {
    return undefined;
  }
  return param.split(',');
}

export function getMultipleValuesSearchParam<FilterType extends string, T extends string>(
  params: URLSearchParams,
  key: FilterType,
  enumType: { [key: string]: T },
): T[] | undefined {
  const values = getMultipleValuesGeneric(params, key);
  return values?.filter((o) => Object.values(enumType).includes(o as T)) as T[];
}

export function getDirectImportTypeParamValue(importType?: DependencyImportTypes) {
  switch (importType) {
    case DependencyImportTypes.Direct:
      return true;
    case DependencyImportTypes.Transitive:
      return false;
    default:
      return undefined;
  }
}

export function getProductionScopeParamValue(scope?: DependencyScopes) {
  switch (scope) {
    case DependencyScopes.Production:
      return true;
    case DependencyScopes.Development:
      return false;
    default:
      return undefined;
  }
}

export function getCvssSeverityText(score: string | null): string | null {
  if (!score) {
    return null;
  }

  const numScore = Number(score);

  if (numScore >= 9.0 && numScore <= 10.0) {
    return 'dependencies.risks.cvss_severity_critical';
  }
  if (numScore >= 7.0 && numScore < 9.0) {
    return 'dependencies.risks.cvss_severity_high';
  }
  if (numScore >= 4.0 && numScore < 7.0) {
    return 'dependencies.risks.cvss_severity_medium';
  }
  if (numScore > 0.0 && numScore < 4.0) {
    return 'dependencies.risks.cvss_severity_low';
  }
  if (numScore === 0.0) {
    return 'dependencies.risks.cvss_severity_none';
  }

  return null;
}
